/**
 * Responsibility: Implement rollback behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState } from "@anyquest/shared-core"
import type { SupervisorIncident } from "@anyquest/shared-core"

export interface RollbackSnapshot {
  id: string
  runId: string
  incidentId: string
  capturedAt: number
  reason: string
  state: GameState
}

export type RollbackSnapshotSummary = Omit<RollbackSnapshot, 'state'>

const MAX_SNAPSHOTS = 60
const snapshots: RollbackSnapshot[] = []

function generateId(): string {
  return Math.random().toString(36).slice(2, 10)
}

export function captureRollbackSnapshot(state: GameState, incident: SupervisorIncident): string {
  const snapshot: RollbackSnapshot = {
    id: generateId(),
    runId: incident.runId,
    incidentId: incident.id,
    capturedAt: Date.now(),
    reason: incident.message,
    state: structuredClone(state),
  }
  snapshots.push(snapshot)
  if (snapshots.length > MAX_SNAPSHOTS) snapshots.shift()
  return snapshot.id
}

export function getRollbackSnapshots(): RollbackSnapshotSummary[] {
  return snapshots.map(({ state: _state, ...rest }) => rest)
}

export function clearRollbackSnapshots() {
  snapshots.length = 0
}

export function discardRollbackSnapshot(snapshotId: string) {
  const idx = snapshots.findIndex(s => s.id === snapshotId)
  if (idx >= 0) snapshots.splice(idx, 1)
}

function replaceStateInPlace(target: GameState, source: GameState) {
  const clone = structuredClone(source) as unknown as Record<string, unknown>
  const root = target as unknown as Record<string, unknown>
  for (const key of Object.keys(root)) delete root[key]
  Object.assign(root, clone)
}

export function restoreRollbackSnapshot(targetState: GameState, snapshotId: string): boolean {
  const snapshot = snapshots.find(s => s.id === snapshotId)
  if (!snapshot) return false
  replaceStateInPlace(targetState, snapshot.state)
  return true
}
